import React, { useEffect, useState } from 'react';
import { Code, Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram } from 'lucide-react';
import { Link } from 'react-router-dom';
import { defaultPublicSettings, getPublicSettings } from '../services/publicSettings';

const Footer = ({ scrollToSection, openBlogs }) => {
  const [settings, setSettings] = useState(defaultPublicSettings);

  useEffect(() => {
    const fetchFooterSettings = async () => {
      try {
        const data = await getPublicSettings();
        setSettings(data);
      } catch (error) {
        console.error('Error fetching footer settings:', error);
      }
    };

    fetchFooterSettings();
  }, []);

  const socialLinks = [
    { key: 'linkedin', href: settings.linkedin, label: 'LinkedIn', icon: Linkedin },
    { key: 'twitter', href: settings.twitter, label: 'Twitter', icon: Twitter },
    { key: 'facebook', href: settings.facebook, label: 'Facebook', icon: Facebook },
    { key: 'instagram', href: settings.instagram, label: 'Instagram', icon: Instagram }
  ].filter((item) => item.href);

  return (
    <footer className="bg-slate-900 text-white">
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
                <Code className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold">{settings.companyName || defaultPublicSettings.companyName}</span>
            </div>
            <p className="text-left text-gray-300 mb-6">
              {settings.tagline || defaultPublicSettings.tagline}
            </p>
            <div className="flex justify-start space-x-4">
              {socialLinks.map(({ key, href, label, icon: Icon }) => (
                <a
                  key={key}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-slate-800 p-3 rounded-lg hover:bg-slate-700 transition-colors"
                  aria-label={label}
                >
                  <Icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {[
                { name: 'Home', id: 'home' },
                { name: 'About', id: 'about' },
                { name: 'Services', id: 'services' },
                { name: 'Industries', id: 'industries' },
                { name: 'Projects', id: 'projects' },
                { name: 'Careers', id: 'careers' },
                { name: 'Contact', id: 'contact' }
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-gray-300 hover:text-cyan-400 transition-colors"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
              <li>
                <button
                  onClick={openBlogs}
                  className="text-gray-300 hover:text-cyan-400 transition-colors"
                >
                  Blogs
                </button>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-lg font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              {[
                'Web Development',
                'Mobile Apps',
                'Cloud Solutions',
                'Automation',
                'AI Solutions',
                'UI/UX Design',
                'Support & Maintenance'
              ].map((service) => (
                <li key={service}>
                  <span className="text-gray-300 hover:text-cyan-400 transition-colors cursor-pointer">
                    {service}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="flex items-center space-x-3">
              <Mail className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm text-gray-400">Email</p>
                <p className="text-white">{settings.contactEmail || defaultPublicSettings.contactEmail}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Phone className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <p className="text-white">{settings.contactPhone || defaultPublicSettings.contactPhone}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <MapPin className="w-5 h-5 text-cyan-400" />
              <div>
                <p className="text-sm text-gray-400">Address</p>
                <p className="text-white">{settings.address || defaultPublicSettings.address}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} {settings.companyName || defaultPublicSettings.companyName}. All rights reserved.
          </p>
          <div className="flex space-x-6 mt-4 md:mt-0">
            <Link to="/privacy" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">
              Terms of Service
            </Link>
            <Link to="/cookies" className="text-gray-400 hover:text-cyan-400 text-sm transition-colors">
              Cookie Policy
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
